import AreaReadDto from '../../../Models/Area/AreaReadDto';
import { MachineReadDto } from '../../../Models/Machine/MachinesReadDto';
import { ScheduleReadDto } from '../../../Models/Schedule/ScheduleReadDto';
import { TaskReadDto } from '../../../Models/Task/TaskReadDto';

const REPORT_LIST_SELECT_ALL_NAME = 'Select All';

export const REPORT_SCHEDULE_SELECT_ALL_ROW = { name: REPORT_LIST_SELECT_ALL_NAME, id: 4 };

export const NO_SCHEDULE_ID = 'no-schedule';
export const REPORT_NO_SCHEDULE_ROW = { id: NO_SCHEDULE_ID, name: 'No Schedule' };

/** Flat Schedule filter tree: root → "No Schedule" row + schedules. */
export function buildScheduleReportTree(rootSelectAllLabel: string, schedules: ScheduleReadDto[]): any[] {
  return [{ name: rootSelectAllLabel, id: 4, items: [REPORT_NO_SCHEDULE_ROW, ...schedules] }];
}

/** Returns root + all items of a flat single-level tree. */
export function allItemsFromFlatTree(tree: any[]): any[] {
  const root = tree[0];
  if (!root) return [];
  return [root, ...(root.items ?? [])];
}

/** Returns schedules that have at least one task linked to any of the given asset IDs. */
export function schedulesForSelectedAssets(
  tasks: TaskReadDto[],
  allSchedules: ScheduleReadDto[],
  selectedAssetIds: Set<string>
): ScheduleReadDto[] {
  const scheduleIds = new Set(
    tasks
      .filter((t) => t.machineId && selectedAssetIds.has(String(t.machineId)) && t.scheduleId)
      .map((t) => String(t.scheduleId))
  );
  return allSchedules
    .filter((s) => scheduleIds.has(String(s.id)))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ─── Nested Location → Asset tree ────────────────────────────────────────────

export const TREE_ROOT_ID = 'root-las';

/**
 * Builds a 2-level nested tree: root → Location → Asset.
 * Location = AreaReadDto; Asset = machines whose areaId matches. Node IDs are
 * prefixed (`loc-`/`asset-`) so they stay unique across levels; empty locations dropped.
 */
export function buildLocationAssetTree(
  rootSelectAllLabel: string,
  areas: AreaReadDto[],
  machines: MachineReadDto[]
): any[] {
  const machinesByArea = new Map<string, MachineReadDto[]>();
  for (const m of machines) {
    const aId = String(m.areaId);
    if (!machinesByArea.has(aId)) machinesByArea.set(aId, []);
    machinesByArea.get(aId)!.push(m);
  }

  const locationNodes = areas
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((area) => {
      const assetNodes = (machinesByArea.get(String(area.id)) ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({ id: `asset-${m.id}`, name: m.name, type: 'Asset' }));
      return { id: `loc-${area.id}`, name: area.name, type: 'Location', items: assetNodes };
    })
    .filter((loc) => loc.items.length > 0);

  return [{ name: rootSelectAllLabel, id: TREE_ROOT_ID, type: 'Root', items: locationNodes }];
}

/** Machine IDs from selected nodes of a `loc-`/`asset-` prefixed tree (asset leaves only). */
export function assetIdsFromTreeSelection(selected: any[]): Set<string> {
  return new Set<string>(
    selected
      .filter((n) => typeof n?.id === 'string' && n.id.startsWith('asset-'))
      .map((n) => String(n.id).slice('asset-'.length))
  );
}

/** Every node of a nested tree (root + all descendants), depth-first — used for the "all selected" state. */
export function allItemsFromNestedTree(tree: any[]): any[] {
  const out: any[] = [];
  const walk = (items: any[]) => {
    for (const item of items ?? []) {
      out.push(item);
      if (item.items?.length) walk(item.items);
    }
  };
  walk(tree);
  return out;
}
